if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      pendingCombinedSelectionContext = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      // LRU cache of section HTML keyed by request URL. Repeated variant
      // selections re-use the cached response, making the variant change feel
      // instant (and shrinking the disabled window of Add to Cart).
      variantResponseCache = new Map();
      static MAX_VARIANT_CACHE_SIZE = 30;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, combinedSelectionContext } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        this.pendingCombinedSelectionContext = combinedSelectionContext || null;

        let productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        if (combinedSelectionContext?.mappedVariantId && target.dataset.productUrl) {
          productUrl = this.appendVariantToUrl(target.dataset.productUrl, combinedSelectionContext.mappedVariantId);
          // When variant is explicit, never send option_values to avoid Shopify
          // resolving selection from stale/default option ids.
          selectedOptionValues = [];
        }
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Skip when the resulting selection (URL + option ids) matches what was
        // last requested — e.g. a re-render that re-fires `change` with the same
        // values. Avoids a redundant fetch and a needless disable/enable flash.
        const optionKey = `${productUrl}|${(selectedOptionValues || []).join(',')}`;
        if (this._lastOptionKey && optionKey === this._lastOptionKey) {
          this.unlockAddToCartButtons();
          this.allProductForms.forEach((form) => form?.toggleSubmitButton(false));
          return;
        }
        this._lastOptionKey = optionKey;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForms = this.allProductForms;
        productForms.forEach((form) => {
          form?.toggleSubmitButton(true);
          form?.handleErrorMessage();
        });
      }

      // Clears the synchronous lock set by VariantSelects.lockAddToCartButtons.
      // The `disabled` attribute itself is owned by toggleSubmitButton (which
      // decides based on variant availability after the section fetch); we only
      // clear the pending flag, aria-disabled, and the inline disabled-look styles.
      unlockAddToCartButtons() {
        delete this.dataset.variantPending;
        this.allProductForms.forEach((form) => {
          form?.querySelectorAll('[type="submit"], button[name="add"]').forEach((btn) => {
            btn.removeAttribute('aria-disabled');
            btn.style.opacity = '';
            btn.style.cursor = '';
            btn.style.pointerEvents = '';
          });
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          const combinedSelectionContext = this.pendingCombinedSelectionContext;
          this.pendingCombinedSelectionContext = null;

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          const resolvedVariantId = combinedSelectionContext?.mappedVariantId || variant?.id;
          this.updateURL(productUrl, resolvedVariantId);

          // Clear the synchronous lock before the swap — the new HTML brings in
          // its own button state. Safe even though `this` will be replaced.
          this.unlockAddToCartButtons();

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        const runCallback = (responseText) => {
          if (signal.aborted) return;
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        };

        const focusTarget = () => {
          if (signal.aborted) return;
          document.querySelector(`#${targetId}`)?.focus();
        };

        const handleError = (error) => {
          if (error?.name === 'AbortError') {
            // A newer selection is already in flight; that handler will own the unlock.
            console.log('Fetch aborted by user');
          } else {
            // Don't leave Add to Cart permanently locked on a network failure.
            this.unlockAddToCartButtons();
            console.error(error);
          }
        };

        // Cache hit: re-render synchronously (in a microtask) using the cached
        // section HTML. Bumps the entry to MRU. Skipped if a newer selection
        // already aborted this one.
        const cachedText = this.variantResponseCache.get(requestUrl);
        if (cachedText) {
          this.variantResponseCache.delete(requestUrl);
          this.variantResponseCache.set(requestUrl, cachedText);
          Promise.resolve()
            .then(() => runCallback(cachedText))
            .then(focusTarget)
            .catch(handleError);
          return;
        }

        fetch(requestUrl, { signal })
          .then((response) => response.text())
          .then((responseText) => {
            // Cache the raw HTML, evicting the LRU entry if over capacity.
            this.variantResponseCache.set(requestUrl, responseText);
            if (this.variantResponseCache.size > ProductInfo.MAX_VARIANT_CACHE_SIZE) {
              const oldestKey = this.variantResponseCache.keys().next().value;
              this.variantResponseCache.delete(oldestKey);
            }
            runCallback(responseText);
          })
          .then(focusTarget)
          .catch(handleError);
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects [data-selected-variant]',
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const currentPath = window.location.pathname;
        const isWithinCollection = currentPath.includes('/collections/');
        const parsedUrl = new URL(url, window.location.origin);
        let pathName = parsedUrl.pathname;

        if (isWithinCollection && !pathName.includes('/collections/')) {
          const match = currentPath.match(/(\/collections\/[^/]+)\//);
          if (match) {
            const collectionPath = match[1];
            const productSegment = pathName.split('/products/').pop();
            pathName = `${collectionPath}/products/${productSegment}`;
          }
        }

        const requestUrl = new URL(pathName, window.location.origin);
        parsedUrl.searchParams.forEach((value, key) => requestUrl.searchParams.set(key, value));

        if (!shouldFetchFullPage) {
          requestUrl.searchParams.set('section_id', this.sectionId);
        } else {
          requestUrl.searchParams.delete('section_id');
        }

        const hasExplicitVariant = requestUrl.searchParams.has('variant');
        if (optionValues.length && !hasExplicitVariant) {
          requestUrl.searchParams.set('option_values', optionValues.join(','));
        } else {
          requestUrl.searchParams.delete('option_values');
        }

        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const combinedSelectionContext = this.pendingCombinedSelectionContext;
          this.pendingCombinedSelectionContext = null;
          const shouldForceSizeSelection =
            combinedSelectionContext?.isCombinedColorChange &&
            combinedSelectionContext?.requestedSizeLabel &&
            !combinedSelectionContext?.wasMapped;
          const effectiveVariantId = shouldForceSizeSelection ? null : variant?.id;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);

          if (!variant) {
            this.updateURL(productUrl, effectiveVariantId);
            this.updateVariantInputs(effectiveVariantId);
            this.setUnavailable();
            // setUnavailable keeps the button disabled, but we still need to
            // strip the loading visuals and clear the pending flag.
            this.unlockAddToCartButtons();
            return;
          }

          // Fallback for combined listings where ID-based map is missing:
          // try selecting equivalent size in the refreshed DOM before clearing variant context.
          if (shouldForceSizeSelection) {
            const didSelectEquivalentSize = this.trySelectEquivalentSize(
              combinedSelectionContext.requestedSizeLabel,
            );
            if (didSelectEquivalentSize) return;
          }

          this.updateURL(productUrl, effectiveVariantId);
          this.updateVariantInputs(effectiveVariantId);

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) =>
            classList.contains('hidden'),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Update the button container to show the correct button based on variant availability
          const buttonContainer = this.querySelector('.product-form__buttons');
          const newButtonContainer = html.querySelector('.product-form__buttons');

          if (buttonContainer && newButtonContainer) {
            buttonContainer.innerHTML = newButtonContainer.innerHTML;
            console.log('Updated button container with new HTML');
          }

          // Also update any mobile/fixed product form buttons
          const mobileNewButtonContainer =
            html.querySelector('.product-form-wrapper-mobile .product-form__buttons') || newButtonContainer;
          const mobileForms = this._mobileProductForms || [];
          mobileForms.forEach((form) => {
            const mobileButtonContainer = form.querySelector('.product-form__buttons');
            if (mobileButtonContainer && mobileNewButtonContainer) {
              mobileButtonContainer.innerHTML = mobileNewButtonContainer.innerHTML;
              console.log('Updated mobile button container with new HTML');
            }
          });

          // Update button states after updating the button structure
          const productForms = this.allProductForms;
          productForms.forEach((form) => {
            // Check if the variant is available by looking for the ProductSubmitButton
            const isVariantAvailable =
              html.getElementById(`ProductSubmitButton-${this.sectionId}`) !== null;

            // Update button visibility first (if the method exists)
            if (form?.updateButtonVisibility) {
              form.updateButtonVisibility(isVariantAvailable);
              console.log(
                `Updated button visibility for form: variant available = ${isVariantAvailable}`,
              );
            } else {
              console.log('Form does not have updateButtonVisibility method');
            }

            // Refresh button references after HTML update
            if (form?.refreshButtonReferences) {
              form.refreshButtonReferences();
            }

            // Then update button states
            form?.toggleSubmitButton(
              html
                .getElementById(`ProductSubmitButton-${this.sectionId}`)
                ?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut,
            );
          });

          // Variant change is fully applied: clear the pending flag and remove
          // loading visuals so the user can submit immediately.
          this.unlockAddToCartButtons();

          // Update notify me modal with new variant information
          this.updateNotifyMeModal(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        // Update main product form inputs
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Update mobile/fixed product form inputs
        const mobileForms = this._mobileProductForms || [];
        mobileForms.forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        const currentPath = window.location.pathname;
        const isWithinCollection = currentPath.includes('/collections/');
        const parsedUrl = new URL(url, window.location.origin);
        const alreadyHasCollection = parsedUrl.pathname.includes('/collections/');
        let newPath = parsedUrl.pathname;

        if (isWithinCollection && !alreadyHasCollection) {
          // Extract the current collection prefix, e.g. /collections/all-women
          const match = currentPath.match(/(\/collections\/[^/]+)/);
          if (match) {
            const collectionPath = match[1];
            const productSegment = parsedUrl.pathname.split('/products/').pop(); // handle
            newPath = `${collectionPath}/products/${productSegment}`;
          }
        }

        const finalUrl = new URL(newPath, window.location.origin);
        parsedUrl.searchParams.forEach((value, key) => finalUrl.searchParams.set(key, value));
        if (variantId) {
          finalUrl.searchParams.set('variant', variantId);
        } else {
          finalUrl.searchParams.delete('variant');
        }
        const finalPathWithSearch = `${finalUrl.pathname}${finalUrl.search}`;

        // Update share button (optional)
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${finalPathWithSearch}`);

        // Update browser history
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', finalPathWithSearch);
      }

      setUnavailable() {
        // When variant is unavailable, we need to show the "Notify Me" button
        // This method is called when no variant is available, so we should ensure
        // the button structure reflects this state
        const productForms = this.allProductForms;
        productForms.forEach((form) => {
          // Show notify me button, hide submit buttons
          if (form?.updateButtonVisibility) {
            form.updateButtonVisibility(false);
          }
          form?.toggleSubmitButton(true, window.variantStrings.unavailable);
        });

        // Note: The actual button structure update should happen in handleUpdateProductInfo
        // when the HTML is fetched and updated. This method is just for the fallback case.

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      applyUnavailableSizeSelectionState(requestedSizeLabel) {
        if (!requestedSizeLabel) return;

        const variantSelects = this.variantSelectors;
        if (!variantSelects) return;

        const sizeContainers = Array.from(
          variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'),
        );
        const unavailableLabel = `${requestedSizeLabel} (Unavailable)`;

        for (const container of sizeContainers) {
          const legend = container.querySelector('legend, label');
          const sizeInputByName = container.querySelector(
            'input[name*="Size"], input[name*="size"], select[name*="Size"], select[name*="size"]',
          );
          const isSizeSelector =
            (legend && legend.textContent.toLowerCase().includes('size')) || !!sizeInputByName;
          if (!isSizeSelector) continue;

          const selectedSwatchValue = container.querySelector('[data-selected-value]');
          if (selectedSwatchValue) {
            selectedSwatchValue.textContent = unavailableLabel;
          }

          const checkedRadio = container.querySelector('input[type="radio"]:checked');
          if (checkedRadio) {
            checkedRadio.checked = false;
            checkedRadio.removeAttribute('checked');
          }

          const selectElement = container.querySelector('select');
          if (selectElement) {
            const selectedOption = selectElement.querySelector('option[selected]');
            if (selectedOption) selectedOption.removeAttribute('selected');

            const placeholderOption = selectElement.querySelector('option[value=""]');
            if (placeholderOption) {
              placeholderOption.setAttribute('selected', 'selected');
              selectElement.value = '';
            } else {
              selectElement.selectedIndex = -1;
            }
          }

          break;
        }

        const sizeErrorMessage = variantSelects.querySelector('.size-selection-error');
        if (sizeErrorMessage) {
          sizeErrorMessage.textContent = `Size ${requestedSizeLabel} is unavailable in this colour. Please select another size.`;
          sizeErrorMessage.style.display = 'block';
        }

        this.allProductForms.forEach((form) => {
          form?.toggleSubmitButton(true, window.variantStrings.unavailable);
        });
      }

      trySelectEquivalentSize(requestedSizeLabel) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects || !requestedSizeLabel) return false;

        const normalizedRequested = this.normalizeSizeLabel(requestedSizeLabel);
        if (!normalizedRequested) return false;

        const sizeContainers = Array.from(
          variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'),
        );

        for (const container of sizeContainers) {
          const legend = container.querySelector('legend, label');
          const sizeInputByName = container.querySelector(
            'input[name*="Size"], input[name*="size"], select[name*="Size"], select[name*="size"]',
          );
          const isSizeSelector =
            (legend && legend.textContent.toLowerCase().includes('size')) || !!sizeInputByName;
          if (!isSizeSelector) continue;

          const radioOptions = Array.from(container.querySelectorAll('input[type="radio"]'));
          const matchingRadio = radioOptions.find((option) => {
            const isUnavailable = option.disabled || option.classList.contains('disabled');
            if (isUnavailable) return false;
            return this.normalizeSizeLabel(option.value) === normalizedRequested;
          });
          if (matchingRadio) {
            matchingRadio.click();
            return true;
          }

          const selectElement = container.querySelector('select');
          if (selectElement) {
            const options = Array.from(selectElement.options);
            const matchingOption = options.find((option) => {
              const isUnavailable =
                option.disabled || option.classList.contains('disabled') || option.value === '';
              if (isUnavailable) return false;
              return this.normalizeSizeLabel(option.value) === normalizedRequested;
            });
            if (matchingOption) {
              selectElement.value = matchingOption.value;
              selectElement.dispatchEvent(new Event('change', { bubbles: true }));
              return true;
            }
          }

          break;
        }

        this.applyUnavailableSizeSelectionState(requestedSizeLabel);
        return false;
      }

      normalizeSizeLabel(value) {
        if (!value) return '';

        const normalized = value.toString().trim().toLowerCase();
        const aliases = {
          'xxx-small': 'xxxs',
          'xx-small': 'xxs',
          'x-small': 'xs',
          small: 's',
          medium: 'm',
          large: 'l',
          'x-large': 'xl',
          'xx-large': 'xxl',
          'xxx-large': 'xxxl',
          'xxxx-large': 'xxxxl',
        };

        return aliases[normalized] || normalized;
      }

      appendVariantToUrl(url, variantId) {
        const parsedUrl = new URL(url, window.location.origin);
        parsedUrl.searchParams.set('variant', variantId);
        return `${parsedUrl.pathname}${parsedUrl.search}`;
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll('li[data-media-id]'),
          );
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.dataset.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        // Find the main product form within this product-info element
        const mainProductForm = this.querySelector(`product-form`);

        // Also find any mobile/fixed product forms outside this element that belong to the same section
        const mobileProductForms = document.querySelectorAll(
          `product-form[data-section-id="${this.dataset.section}"]`,
        );

        // Return the main form, but also store references to mobile forms for updating
        if (mainProductForm) {
          // Store mobile forms reference for later use
          this._mobileProductForms = Array.from(mobileProductForms).filter(
            (form) => form !== mainProductForm,
          );
        }

        return mainProductForm;
      }

      // Helper method to get all product forms (main + mobile/fixed)
      get allProductForms() {
        const mainForm = this.productForm;
        const mobileForms = this._mobileProductForms || [];
        return [mainForm, ...mobileForms].filter(Boolean);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // Method to update notify me modal with new variant information
      updateNotifyMeModal(variant) {
        if (!variant) return;

        const modal = document.querySelector('.notify-me-modal');
        if (!modal) return;

        // Update variant ID in the form
        const variantIdInput = modal.querySelector('input[name="variantId"]');
        if (variantIdInput) {
          const variantId = variant.id.toString().split('/').pop();
          variantIdInput.value = variantId;
        }

        // Update product info display
        const productTitle = modal.querySelector('.notify-me-product-info__value');
        if (productTitle) {
          productTitle.textContent = this.dataset.productTitle || 'Product';
        }

        // Update option1 if available
        if (variant.option1) {
          const option1Value = modal.querySelector(
            '.notify-me-product-info__row:nth-child(2) .notify-me-product-info__value',
          );
          if (option1Value) {
            option1Value.textContent = variant.option1;
          }
        }

        // Update option2 if available
        if (variant.option2) {
          const option2Value = modal.querySelector(
            '.notify-me-product-info__row:nth-child(3) .notify-me-product-info__value',
          );
          if (option2Value) {
            option2Value.textContent = variant.option2;
          }
        }

        console.log('Updated notify me modal with variant:', variant);
      }
    },
  );
}
